import { canDownloadRepoItem } from "@/components/features/matter/file-repo/fileRepositoryDownload";
import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import {
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui";
import type { RepoItem } from "@/components/features/matter/file-repo/FileRepository";
import type { Matter } from "@/lib/types";
import {
  ChevronLeft,
  ChevronRight,
  CopyPlus,
  Download,
  FileAxis3D,
  Folder,
  FolderInput,
  PlusCircle,
  Share2,
} from "lucide-react";
import { useState, type ReactNode } from "react";

interface MatterFileContextMenuProps {
  canDelete: boolean;
  canDownload: boolean;
  isDownloading: boolean;
  canEdit: boolean;
  canMerge: boolean;
  canMove: boolean;
  canDuplicate: boolean;
  duplicateItems: RepoItem[];
  canShare: boolean;
  destinationFolders: {
    canMoveHere: boolean;
    folder: RepoItem;
  }[];
  items: RepoItem[];
  matter?: Pick<Matter, "id">;
  onDelete: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onMerge: () => void;
  onCreateFolder: () => void;
  onMove: (folderId: string) => void;
  onDuplicate: () => void;
  rootFolderName: string;
  showRootFolder: boolean;
  onShare: () => void;
  trigger: ReactNode;
}

const moveMenuContentClassName =
  "max-h-[min(60vh,420px)] w-[min(280px,calc(100vw-2rem))] overflow-y-auto";

export const MatterFileContextMenu = ({
  canDelete,
  canDownload,
  isDownloading,
  canEdit,
  canMerge,
  canMove,
  canDuplicate,
  duplicateItems,
  canShare,
  destinationFolders,
  items,
  matter,
  onDelete,
  onDownload,
  onEdit,
  onMerge,
  onCreateFolder,
  onMove,
  onDuplicate,
  rootFolderName,
  showRootFolder,
  onShare,
  trigger,
}: MatterFileContextMenuProps) => {
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const [currentDestinationFolderId, setCurrentDestinationFolderId] = useState<
    string | null
  >(null);
  const isBulkAction = items.length > 1;
  const item = items[0];
  const downloadableItems = items.filter(canDownloadRepoItem);
  const hasDownloadFolders = downloadableItems.some((item) => item.isFolder);
  const shareableItems = items.filter(
    (candidate) => !candidate.isFolder && candidate.kind !== "share-folder",
  );
  const canOpen = Boolean(!isBulkAction && canEdit);
  const canViewEventLog = Boolean(
    matter && !isBulkAction && item.kind !== "share-folder",
  );
  const hasFileActions =
    (canDownload && downloadableItems.length > 0) ||
    (canShare && shareableItems.length > 0);
  const hasCustomActions =
    hasFileActions || canMerge || canMove || canDuplicate;
  const currentDestinationFolder = destinationFolders.find(
    ({ folder }) => folder.id === currentDestinationFolderId,
  );
  const currentDestinationChildren = destinationFolders.filter(({ folder }) => {
    if (currentDestinationFolderId) {
      return (folder.idLocation ?? "") === currentDestinationFolderId;
    }

    return (
      !folder.idLocation ||
      !destinationFolders.some(
        ({ folder: candidate }) => candidate.id === folder.idLocation,
      )
    );
  });
  const parentDestinationFolder = currentDestinationFolder?.folder.idLocation
    ? destinationFolders.find(
        ({ folder }) =>
          folder.id === currentDestinationFolder.folder.idLocation,
      )
    : undefined;
  const currentDestinationPath = (() => {
    if (!currentDestinationFolder) return rootFolderName;

    const segments: string[] = [];
    let destination: typeof currentDestinationFolder | undefined =
      currentDestinationFolder;

    while (destination) {
      segments.unshift(destination.folder.name);
      const parentId: string | null | undefined = destination.folder.idLocation;
      destination = parentId
        ? destinationFolders.find(({ folder }) => folder.id === parentId)
        : undefined;
    }

    return segments.join(" / ");
  })();

  return (
    <>
      <ContextMenu
        entityName={item.isFolder ? "folder" : "file"}
        onOpenChange={(open) => {
          if (!open) setCurrentDestinationFolderId(null);
        }}
        open={canOpen ? { onSelect: onEdit } : false}
        copy={
          !isBulkAction
            ? {
                label: item.isFolder ? undefined : "Copy file name",
                value: item.name,
                successLabel: item.isFolder ? "Folder name" : "File name",
              }
            : false
        }
        delete={
          canDelete
            ? {
                label: isBulkAction
                  ? `Delete ${items.length} items`
                  : undefined,
                onSelect: onDelete,
              }
            : false
        }
        trigger={trigger}
        contentClassName="min-w-[220px]"
      >
        {hasCustomActions ? (
          <>
            {canDuplicate ? (
              <ContextMenuItem onSelect={onDuplicate}>
                <CopyPlus />
                {duplicateItems.length > 1
                  ? `Duplicate ${duplicateItems.length} items`
                  : `Duplicate ${duplicateItems[0]?.isFolder ? "folder" : "file"}`}
              </ContextMenuItem>
            ) : null}
            {canDownload && downloadableItems.length > 0 ? (
              <ContextMenuItem onSelect={onDownload} disabled={isDownloading}>
                <Download />
                {isBulkAction
                  ? `Download ${downloadableItems.length} ${hasDownloadFolders ? "items" : "files"} as ZIP`
                  : item.isFolder
                    ? "Download folder as ZIP"
                    : "Download file"}
              </ContextMenuItem>
            ) : null}
            {canShare && shareableItems.length > 0 ? (
              <ContextMenuItem onSelect={onShare}>
                <Share2 />
                {isBulkAction
                  ? `Share ${shareableItems.length} files`
                  : "Share file"}
              </ContextMenuItem>
            ) : null}
            {canMerge ? (
              <>
                {hasFileActions ? <ContextMenuSeparator /> : null}
                <ContextMenuItem onSelect={onMerge}>
                  <FileAxis3D />
                  Merge {items.length} files
                </ContextMenuItem>
              </>
            ) : null}
            {canMove ? (
              <>
                {hasFileActions || canMerge ? <ContextMenuSeparator /> : null}
                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <FolderInput />
                    Move to folder
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className={moveMenuContentClassName}>
                    {currentDestinationFolder ? (
                      <>
                        <ContextMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            setCurrentDestinationFolderId(
                              parentDestinationFolder?.folder.id ?? null,
                            );
                          }}
                        >
                          <ChevronLeft />
                          Back
                        </ContextMenuItem>
                        {currentDestinationFolder.canMoveHere ? (
                          <ContextMenuItem
                            className="items-start"
                            onSelect={() => {
                              onMove(currentDestinationFolder.folder.id);
                            }}
                          >
                            <FolderInput />
                            <span className="break-words whitespace-normal">
                              Move to &quot;{currentDestinationPath}&quot;
                            </span>
                          </ContextMenuItem>
                        ) : null}
                        <ContextMenuSeparator />
                      </>
                    ) : showRootFolder ? (
                      <ContextMenuItem
                        onSelect={() => {
                          onMove("");
                        }}
                      >
                        <Folder className="text-amber-500" />
                        <span className="break-words whitespace-normal">
                          Move to &quot;{rootFolderName}&quot;
                        </span>
                      </ContextMenuItem>
                    ) : null}
                    {currentDestinationChildren.map((destination) => (
                      <ContextMenuItem
                        key={destination.folder.id}
                        onSelect={(event) => {
                          event.preventDefault();
                          setCurrentDestinationFolderId(destination.folder.id);
                        }}
                      >
                        <Folder className="text-amber-500" />
                        <span className="min-w-0 flex-1 truncate">
                          {destination.folder.name}
                        </span>
                        <ChevronRight className="ml-auto" />
                      </ContextMenuItem>
                    ))}
                    {!currentDestinationFolder ? (
                      <ContextMenuItem onSelect={onCreateFolder}>
                        <PlusCircle />
                        Add a folder
                      </ContextMenuItem>
                    ) : null}
                  </ContextMenuSubContent>
                </ContextMenuSub>
              </>
            ) : null}
          </>
        ) : null}
        {canViewEventLog ? (
          <MatterSubItemEventLogsContextMenu
            onOpenEventLogs={() => {
              setIsEventLogsOpen(true);
            }}
          />
        ) : null}
      </ContextMenu>
      {canViewEventLog && matter ? (
        <MatterSubItemEventLogsModal
          entityLabel={item.isFolder ? "folder" : "file"}
          matter={matter}
          onOpenChange={setIsEventLogsOpen}
          open={isEventLogsOpen}
          subItem={{ id: item.id, type: "file" }}
        />
      ) : null}
    </>
  );
};
